import { useEffect, useRef, useState } from 'preact/hooks'
import { useLocation } from 'wouter-preact'
import { READINGS_PER_SITE } from '~/calculations/measurementQuality'
import { SolaMark } from '~/components/identity/SolaMark'
import { Button } from '~/components/primitives/Button'
import { DateField, NumericField, RadioGroup, TextField } from '~/components/primitives/Field'
import { InfoButton } from '~/components/primitives/InfoButton'
import { InfoSheet } from '~/components/primitives/InfoSheet'
import { DOMAIN_SELECTION_PRESET_LABELS, orderDomains, proposalFor } from '~/models/domainPreset'
import {
  DOMAIN_INTENTION_LABELS,
  DOMAIN_INTENTIONS,
  DOMAIN_PRIORITY_LABELS,
  FITNESS_DOMAIN_LABELS,
  FITNESS_DOMAINS,
  type FitnessDomain,
  isFitnessDomain,
} from '~/models/fitnessDomain'
import {
  isCalorieTargetBasis,
  isNutritionSource,
  NUTRITION_SOURCE_LABELS,
  NUTRITION_SOURCES,
} from '~/models/nutritionPlan'
import { PHASE_LABELS } from '~/models/phase'
import { PHOTO_VIEW_LABELS, PHOTO_VIEWS, type PhotoView } from '~/models/photo'
import { DESIRED_OUTCOME_LABELS, DESIRED_OUTCOMES, type DesiredOutcome } from '~/models/project'
import { isRouteRecommendation, type RouteRecommendation } from '~/models/routeRecommendation'
import { PhotoCapture } from '~/pages/photos/PhotoCapture'
import { type RouteOutlook, routeOutlook } from '~/rules/route/outlook'
import {
  TRAINING_EXPERIENCE_LABELS,
  TRAINING_EXPERIENCE_LEVELS,
  type TrainingExperience,
} from '~/rules/route/snapshot'
import { savePhoto } from '~/services/photoService'
import {
  buildSetupStarterCommitments,
  clearProjectSetupDraft,
  completeProjectSetup,
  EMPTY_SETUP_DRAFT,
  latestSetupStartDate,
  loadProjectSetupDraft,
  type ProjectSetupDraft,
  recommendRouteForSetup,
  resumeStepFor,
  SETUP_STEP_TITLES,
  type SetupMeasurement,
  type SetupStep,
  saveProjectSetupDraft,
  selectedDomainsOf,
  setupErrorMessage,
  stepsFor,
  willPlanBodyComposition,
} from '~/services/projectSetupService'
import { useDatabase } from '~/storage/DatabaseContext'
import type { SolaDatabase } from '~/storage/db'
import { getActivePhase } from '~/storage/repositories/phaseRepository'
import { getActiveProject } from '~/storage/repositories/projectRepository'
import { calendarDateFrom, toCalendarDate, unwrapDate } from '~/types/dates'
import type { PhaseId, ProjectId } from '~/types/ids'
import { formatApproximateMonth, formatWeekCount } from '~/utils/format'
import styles from './ProjectSetupPage.module.css'

/**
 * Project Setup (D-062, D-063, D-090).
 *
 * ────────────────────────────────────────────────────────────────────────────
 *  THIS SCREEN ASKS WHERE YOU WANT TO GET TO. IT NEVER ASKS HOW.
 * ────────────────────────────────────────────────────────────────────────────
 *
 * There is no phase question anywhere in this file, and there must never be
 * one. D-063 removed manual phase selection because asking it first inverts the
 * product: choosing between a cut, a maintenance block and a lean bulk is
 * precisely what the user cannot do, and is the reason the route engine exists.
 * The phase record is still created — its type and range come from the
 * recommendation rather than from a radio group.
 *
 * ────────────────────────────────────────────────────────────────────────────
 *  THE STEP LIST IS COMPUTED, AND THAT IS C6.1's WHOLE PRODUCT CHANGE
 * ────────────────────────────────────────────────────────────────────────────
 *
 * The user picks which areas they want to work on, and the steps follow from
 * that. A cardio-only user is **never asked** for a waist, a neck or a photo,
 * because those steps are not in their list — not because they are shown and
 * skippable. `stepsFor` is the single authority; nothing here decides it again.
 *
 * A preset proposes a set and the user confirms the individual domains. Nothing
 * enrols them (D-090), and reaching the end still creates nothing until the
 * button is pressed (D-020).
 */

/**
 * The phase and project a starting photo belongs to.
 *
 * Read back after `completeProjectSetup` rather than returned by it: the setup
 * transaction has one job and threading ids out of it to serve a later, optional
 * write would widen its contract for no gain. Absent ids are simply omitted, so
 * a photo is never written pointing at a phase that does not exist.
 */
async function activeOwners(
  db: SolaDatabase,
): Promise<{ phaseId?: PhaseId; projectId?: ProjectId }> {
  const [phase, project] = await Promise.all([getActivePhase(db), getActiveProject(db)])
  const phaseId = phase.ok ? phase.value?.id : undefined
  const projectId = project.ok ? project.value?.id : undefined
  return {
    ...(phaseId === undefined ? {} : { phaseId }),
    ...(projectId === undefined ? {} : { projectId }),
  }
}

export function ProjectSetupPage() {
  const db = useDatabase()
  const [, navigate] = useLocation()
  const today = unwrapDate(calendarDateFrom(new Date()))

  const [draft, setDraft] = useState<ProjectSetupDraft>(EMPTY_SETUP_DRAFT)
  const [loaded, setLoaded] = useState(false)
  const [recommendation, setRecommendation] = useState<RouteRecommendation | undefined>(undefined)
  /* D-102. Derived beside the recommendation and held in component state only —
     it is shown and stored nowhere, and `Project.recommendationSnapshot` has no
     field that could carry it even if someone tried. */
  const [outlook, setOutlook] = useState<RouteOutlook | undefined>(undefined)
  const [measurement, setMeasurement] = useState<SetupMeasurement | undefined>(undefined)
  const [error, setError] = useState<string | undefined>(undefined)
  const [busy, setBusy] = useState(false)
  /* The confirmation sheet, and the control to hand focus back to. Supplied
     rather than read from `document.activeElement`, which is `<body>` on iOS. */
  const [confirming, setConfirming] = useState(false)
  const confirmRef = useRef<HTMLButtonElement | null>(null)

  /* Held in memory, never in the draft. See PhotosStep: writing one before the
     user confirms would break the D-020 guarantee that reaching the
     recommendation creates nothing. */
  const [startingPhotos, setStartingPhotos] = useState<Partial<Record<PhotoView, Blob>>>({})
  /* The project exists but a photo did not save. Not a setup failure — the
     project is real — so this offers a way on rather than a way back. */
  const [photosFailed, setPhotosFailed] = useState(false)

  /* Resume where the user left off. iOS evicts a backgrounded tab, so a
     multi-step form that lost its place on every interruption would be
     abandoned — which is why the draft exists at all (D-062). */
  useEffect(() => {
    let cancelled = false
    void (async () => {
      const stored = await loadProjectSetupDraft(db)
      if (cancelled) return
      if (stored.ok && stored.value !== undefined) {
        /* `resumeStepFor` may move the user back: a draft can point at a step the
           current selection no longer includes, which is reachable by going back
           and removing a domain. Nearest earlier, never the beginning. */
        setDraft({ ...stored.value, stepKey: resumeStepFor(stored.value) })
      }
      setLoaded(true)
    })()
    return () => {
      cancelled = true
    }
  }, [db])

  const steps = stepsFor(draft)
  const step = draft.stepKey
  const position = steps.indexOf(step)
  const plansBody = willPlanBodyComposition(draft)

  function update(patch: Partial<ProjectSetupDraft>) {
    setDraft((current) => ({ ...current, ...patch }))
    setError(undefined)
  }

  /**
   * Move to a step and record it.
   *
   * **The step being moved TO.** Recording the one being left sends the user
   * back one place on every resume — the defect the weekly wizard produced at
   * Checkpoint 2.1, and the same shape of mistake is available here.
   */
  async function goToStep(next: SetupStep) {
    const moved = { ...draft, stepKey: next }
    setDraft(moved)
    setError(undefined)
    await saveProjectSetupDraft(db, moved)
  }

  /**
   * The next and previous step in **this user's** list.
   *
   * Computed from `steps` rather than by adding one to an index, because the list
   * changes as the selection changes: a user who adds mobility on the first step
   * has a different neighbour than one who did not.
   */
  function neighbour(offset: number): SetupStep | undefined {
    const candidate = steps[position + offset]
    return candidate
  }

  async function next() {
    const issue = validateStep(step, draft, today)
    if (issue !== undefined) {
      setError(issue)
      return
    }

    const following = neighbour(1)
    if (following === undefined) return

    /* Entering the confirmation is where the route is asked for, and only when
       body composition is active — no other domain has an engine to ask. */
    if (following === 'confirm' && plansBody) {
      await requestRecommendation()
      return
    }
    await goToStep(following)
  }

  async function requestRecommendation() {
    setBusy(true)
    const result = await recommendRouteForSetup(db, draft, new Date())
    setBusy(false)

    if (!result.ok) {
      setError(setupErrorMessage(result.error))
      return
    }
    if (!isRouteRecommendation(result.value.outcome)) {
      /* The engine has nothing to say and says so. It carries no route and no
         confidence, and is never presented as one (D-071). */
      setError(
        `Project Sola cannot recommend a route yet. It still needs: ${result.value.outcome.missingEvidence.join('; ')}.`,
      )
      return
    }

    setMeasurement(result.value.measurement)
    setRecommendation(result.value.outcome)
    setOutlook(
      routeOutlook({ recommendation: result.value.outcome, snapshot: result.value.snapshot }),
    )
    await goToStep('confirm')
  }

  async function confirm() {
    // A body-composition setup cannot be confirmed without its route.
    if (plansBody && (recommendation === undefined || measurement === undefined)) return

    setBusy(true)
    const created = await completeProjectSetup(
      db,
      {
        draft,
        ...(recommendation === undefined ? {} : { recommendation }),
        ...(measurement === undefined ? {} : { measurement }),
      },
      new Date(),
    )
    setBusy(false)

    if (!created.ok) {
      setError(setupErrorMessage(created.error))
      return
    }

    /**
     * The starting photos, written only now.
     *
     * **A photo that fails here does not undo the project.** The project, its
     * phase, its goal and the first measurement session committed atomically a
     * moment ago and are correct; reporting the whole setup as failed would be
     * false and would invite the user to run it again. So the failure is
     * reported for what it is — some photos did not save — and the user is
     * offered the way forward, with the Photos screen still able to take them.
     */
    const savedAll = await saveStartingPhotos()
    if (!savedAll) {
      setPhotosFailed(true)
      return
    }
    navigate('/today')
  }

  /** Returns whether every captured photo was written. */
  async function saveStartingPhotos(): Promise<boolean> {
    const entries = Object.entries(startingPhotos) as [PhotoView, Blob][]
    if (entries.length === 0) return true

    setBusy(true)
    const owners = await activeOwners(db)
    const results: boolean[] = []
    for (const [view, file] of entries) {
      const saved = await savePhoto(db, {
        file,
        view,
        source: 'guidedCapture',
        now: new Date(),
        ...owners,
      })
      results.push(saved.ok)
    }
    setBusy(false)
    return results.every(Boolean)
  }

  async function startOver() {
    await clearProjectSetupDraft(db)
    setDraft(EMPTY_SETUP_DRAFT)
    setRecommendation(undefined)
    setMeasurement(undefined)
    setError(undefined)
  }

  if (!loaded) {
    return (
      <main id="main-content" class={styles.screen} tabIndex={-1}>
        <p class={styles.lede}>Loading…</p>
      </main>
    )
  }

  const isFirst = position <= 0
  const onConfirm = step === 'confirm'

  return (
    <main id="main-content" class={styles.screen} tabIndex={-1}>
      {/* First step only (D-089). This is the one place in the app that introduces
          itself, and it belongs here rather than on a welcome screen because
          Project Sola does not ask for a tap that collects nothing. It lives in the
          page shell rather than inside the step component because every step shares
          the same `#step-heading` pattern. */}
      {isFirst ? (
        <div class={styles.brand}>
          <SolaMark size="lg" />
          <p class={styles.brandName}>Project Sola</p>
        </div>
      ) : null}

      <p class={styles.progress} aria-live="polite">
        {onConfirm
          ? 'Your plan'
          : `Step ${position + 1} of ${steps.length} — ${SETUP_STEP_TITLES[step]}`}
      </p>

      <StepBody
        step={step}
        draft={draft}
        today={today}
        recommendation={recommendation}
        outlook={outlook}
        onChange={update}
        captured={startingPhotos}
        onCaptured={(view, file) => setStartingPhotos((current) => ({ ...current, [view]: file }))}
        onRemove={(view) =>
          setStartingPhotos((current) => {
            const remaining = { ...current }
            delete remaining[view]
            return remaining
          })
        }
      />

      {error === undefined ? null : (
        <p class={styles.error} role="alert">
          {error}
        </p>
      )}

      {photosFailed ? (
        <p class={styles.error} role="alert">
          Your project was created. Some of your starting photos could not be saved — you can take
          them again from the Photos tab.
        </p>
      ) : null}

      <div class={styles.actions}>
        {!isFirst && !photosFailed ? (
          <Button
            variant="secondary"
            onClick={() => {
              const previous = neighbour(-1)
              if (previous !== undefined) void goToStep(previous)
            }}
          >
            Back
          </Button>
        ) : null}

        {photosFailed ? (
          <Button onClick={() => navigate('/today')} fullWidth>
            Continue
          </Button>
        ) : onConfirm ? (
          <Button
            onClick={() => setConfirming(true)}
            elementRef={(element) => {
              confirmRef.current = element
            }}
          >
            Start this project
          </Button>
        ) : (
          <Button onClick={() => void next()} busy={busy} fullWidth={isFirst}>
            {step === 'photos' && Object.keys(startingPhotos).length === 0
              ? 'Skip for now'
              : 'Continue'}
          </Button>
        )}
      </div>

      {onConfirm ? (
        <div class={styles.actions}>
          <Button variant="secondary" onClick={() => void startOver()}>
            Start setup again
          </Button>
        </div>
      ) : null}

      {/*
        The confirmation sheet (D-104 §6).
        ────────────────────────────────────────────────────────────────────────
        **Nothing is written until the button inside it**, and dismissing starts
        nothing — Escape, Close and the scrim all just close it, which is the
        `InfoSheet` guarantee rather than something re-implemented here. That is
        what keeps D-020 true through a second control: the project begins on an
        explicit action and on nothing else.

        **This is not the destructive-action idiom** (D-104 §3). Delete, reset
        and ending a phase keep their inline arm-then-confirm block, because a
        backdrop tap must not be a way to lose a confirmation for something with
        no undo. Starting a project is reversible — it can be ended, and nothing
        is lost by dismissing this — so a sheet is the right shape for it.

        **The consequential warning lives here rather than on the screen behind**,
        which is what let the confirmation screen become scannable without any
        of it being deleted.
      */}
      <InfoSheet
        open={confirming}
        title="Start this project?"
        busy={busy}
        returnFocusTo={confirmRef}
        onClose={() => setConfirming(false)}
      >
        <p>
          This creates your project and its first phase, and saves the measurements you entered.
          Project Sola will start judging your weeks against it.
        </p>
        <p>
          You can end a project at any time from Settings, and ending one keeps everything it
          recorded. What it does not do is start the next one for you.
        </p>
        <p>Nothing has been created yet. The button below is what begins it.</p>
        <Button onClick={() => void confirm()} busy={busy} busyLabel="Creating…" fullWidth>
          Create my project
        </Button>
      </InfoSheet>
    </main>
  )
}

/* ── Step dispatch ──────────────────────────────────────────────────────── */

interface StepProps {
  draft: ProjectSetupDraft
  onChange: (patch: Partial<ProjectSetupDraft>) => void
}

interface StepBodyProps extends StepProps {
  readonly step: SetupStep
  readonly today: string
  readonly recommendation: RouteRecommendation | undefined
  readonly outlook: RouteOutlook | undefined
  readonly captured: Partial<Record<PhotoView, Blob>>
  readonly onCaptured: (view: PhotoView, file: Blob) => void
  readonly onRemove: (view: PhotoView) => void
}

/**
 * One step, chosen by key.
 *
 * Extracted from the page for the reason D-056 requires rather than for tidiness:
 * a nine-branch conditional inside a component already holding the draft, the
 * recommendation, the photos and the busy state trips the complexity rule, and
 * suppressing it in a screen that decides what a user is asked for their body is
 * not acceptable.
 */
function StepBody(props: StepBodyProps) {
  const { step, draft, today, recommendation, onChange } = props

  if (step === 'domains') return <DomainsStep draft={draft} onChange={onChange} />
  if (step === 'confirmPreset') return <ConfirmPresetStep draft={draft} onChange={onChange} />
  if (step === 'intentions') return <IntentionsStep draft={draft} onChange={onChange} />
  if (step === 'aboutYou') return <AboutYouStep draft={draft} today={today} onChange={onChange} />
  if (step === 'measurements') return <MeasurementsStep draft={draft} onChange={onChange} />
  if (step === 'photos') {
    return (
      <PhotosStep
        captured={props.captured}
        onCaptured={props.onCaptured}
        onRemove={props.onRemove}
      />
    )
  }
  if (step === 'training') return <TrainingStep draft={draft} onChange={onChange} />
  if (step === 'destination') {
    return <DestinationStep draft={draft} today={today} onChange={onChange} />
  }
  if (step === 'nutrition') return <NutritionStep draft={draft} onChange={onChange} />
  return (
    <PlanStep
      draft={draft}
      recommendation={recommendation}
      outlook={props.outlook}
      onChange={onChange}
    />
  )
}

/* ── Steps ──────────────────────────────────────────────────────────────── */

/**
 * What the user wants to work on (D-090).
 *
 * **The first question in the app, and it decides every question after it.**
 * Multi-select rather than a radio group, because "body composition and cardio" is
 * an ordinary answer. The preset is offered beside the list rather than inside it:
 * it is a shortcut to a selection, not a fifth area.
 */
function DomainsStep({ draft, onChange }: StepProps) {
  const selected = new Set(draft.selectedDomains)

  function toggle(domain: FitnessDomain) {
    const next = new Set(selected)
    if (next.has(domain)) next.delete(domain)
    else next.add(domain)
    /* Choosing by hand clears the preset flag, so the confirmation step does not
       appear for a selection the user made themselves. */
    onChange({ selectedDomains: [...next], usedPreset: false })
  }

  function applyPreset() {
    const proposal = proposalFor('overallFitness')
    onChange({
      selectedDomains: proposal.map((entry) => entry.domain),
      intentions: Object.fromEntries(proposal.map((entry) => [entry.domain, entry.intention])),
      priorities: Object.fromEntries(proposal.map((entry) => [entry.domain, entry.priority])),
      usedPreset: true,
    })
  }

  return (
    <section aria-labelledby="step-heading">
      <h1 id="step-heading" class={styles.title}>
        What do you want to work on?
      </h1>
      <p class={styles.lede}>
        Pick as many as you like. Project Sola only asks about the areas you choose, and only tracks
        those — you can add or pause one later without starting over.
      </p>

      <ul class={styles.domainList}>
        {FITNESS_DOMAINS.map((domain) => (
          <li key={domain}>
            <button
              type="button"
              class={styles.domainOption}
              aria-pressed={selected.has(domain)}
              onClick={() => toggle(domain)}
            >
              <span class={styles.domainName}>{FITNESS_DOMAIN_LABELS[domain]}</span>
              <span class={styles.domainHint}>{DOMAIN_DESCRIPTIONS[domain]}</span>
            </button>
          </li>
        ))}
      </ul>

      <p class={styles.hint}>Not sure? Start from a suggestion and change it on the next screen.</p>
      <Button variant="secondary" onClick={applyPreset}>
        Suggest {DOMAIN_SELECTION_PRESET_LABELS.overallFitness.toLowerCase()}
      </Button>
    </section>
  )
}

/** Plain descriptions, so the choice does not require knowing the vocabulary. */
const DOMAIN_DESCRIPTIONS: Readonly<Record<FitnessDomain, string>> = {
  bodyComposition: 'Body fat, weight and how you look. Uses weigh-ins and measurements.',
  cardio: 'Breathing, endurance and resting heart rate. Uses sessions and steps.',
  strength: 'How much you can lift, tracked on a few lifts you choose.',
  mobility: 'Range of movement and stiffness. Tracked as sessions you did.',
}

/**
 * The preset's proposal, shown for review (D-090).
 *
 * **Project Sola may recommend. It may never enrol.** The proposal is on screen as
 * a list the user edits, and the step exists precisely so that "overall fitness"
 * resolves into areas they agreed to rather than into a stored goal nothing can
 * measure.
 */
function ConfirmPresetStep({ draft, onChange }: StepProps) {
  const selected = selectedDomainsOf(draft)

  return (
    <section aria-labelledby="step-heading">
      <h1 id="step-heading" class={styles.title}>
        Here is what that means
      </h1>
      <p class={styles.lede}>
        A suggestion, not a decision. Remove anything you do not want — Project Sola will not track
        an area you take off this list.
      </p>

      <ul class={styles.domainList}>
        {selected.map((domain) => (
          <li key={domain} class={styles.domainRow}>
            <span class={styles.domainName}>{FITNESS_DOMAIN_LABELS[domain]}</span>
            <span class={styles.domainHint}>
              {
                DOMAIN_PRIORITY_LABELS[
                  (draft.priorities[domain] as keyof typeof DOMAIN_PRIORITY_LABELS) ?? 'none'
                ]
              }
            </span>
            <Button
              variant="secondary"
              label={`Remove ${FITNESS_DOMAIN_LABELS[domain].toLowerCase()}`}
              onClick={() =>
                onChange({
                  selectedDomains: draft.selectedDomains.filter((entry) => entry !== domain),
                })
              }
            >
              Remove
            </Button>
          </li>
        ))}
      </ul>

      <p class={styles.pending}>
        One main focus with one supporting area is what Project Sola suggests, because working on
        everything at once usually means progress on none of it. You can change any of this on the
        next screen.
      </p>
    </section>
  )
}

/**
 * How much the user wants to do about each area (D-090).
 *
 * Intention and priority per domain. **`trackOnly` is a real answer**, not a
 * weaker `maintain`: it collects the evidence and plans nothing, which is the
 * honest option for an area someone wants to watch without adding work.
 */
function IntentionsStep({ draft, onChange }: StepProps) {
  const selected = selectedDomainsOf(draft)

  return (
    <section aria-labelledby="step-heading">
      <h1 id="step-heading" class={styles.title}>
        How much for each?
      </h1>
      <p class={styles.lede}>
        Improving something means Project Sola plans it and reviews it weekly. Maintaining means it
        watches for a decline. Tracking only means it records the numbers and asks nothing of you.
      </p>

      {selected.map((domain) => (
        <RadioGroup
          key={domain}
          legend={FITNESS_DOMAIN_LABELS[domain]}
          value={draft.intentions[domain]}
          onChange={(intention) =>
            onChange({ intentions: { ...draft.intentions, [domain]: intention } })
          }
          options={SELECTABLE_INTENTIONS.map((intention) => ({
            value: intention,
            label: DOMAIN_INTENTION_LABELS[intention],
          }))}
        />
      ))}

      <p class={styles.pending}>
        Project Sola suggests one main focus. Whichever area you pick first below becomes it; the
        rest support it or are simply protected.
      </p>

      <RadioGroup
        legend="Which is your main focus?"
        value={primaryOf(draft)}
        onChange={(domain) => onChange({ priorities: prioritiesWithPrimary(draft, domain) })}
        options={selected
          .filter((domain) => draft.intentions[domain] !== 'trackOnly')
          .map((domain) => ({ value: domain, label: FITNESS_DOMAIN_LABELS[domain] }))}
      />
    </section>
  )
}

/* `inactive` is representable on a stored record so a domain can be switched off
   without losing its history, but it is not offered here — not choosing an area on
   the previous step is how you say no to it. Offering both would be two controls
   for one decision. */
const SELECTABLE_INTENTIONS = DOMAIN_INTENTIONS.filter((intention) => intention !== 'inactive')

function primaryOf(draft: ProjectSetupDraft): string | undefined {
  return Object.entries(draft.priorities).find(([, priority]) => priority === 'primary')?.[0]
}

/**
 * Make one domain primary, and demote whatever was.
 *
 * A `maintain` domain becomes `protect` rather than `supporting`, because
 * supporting implies it is being worked on. Everything else supports.
 */
function prioritiesWithPrimary(draft: ProjectSetupDraft, primary: string): Record<string, string> {
  const next: Record<string, string> = {}
  for (const domain of selectedDomainsOf(draft)) {
    if (domain === primary) next[domain] = 'primary'
    else if (draft.intentions[domain] === 'trackOnly') next[domain] = 'none'
    else if (draft.intentions[domain] === 'maintain') next[domain] = 'protect'
    else next[domain] = 'supporting'
  }
  return next
}

function AboutYouStep({ draft, today, onChange }: StepProps & { today: string }) {
  return (
    <section aria-labelledby="step-heading">
      <h1 id="step-heading" class={styles.title}>
        About you
      </h1>
      <p class={styles.lede}>
        Project Sola needs a little context before it can plan anything. This is the only
        information it requires.
      </p>

      <NumericField
        label="Height"
        hint="Measured without shoes. The recommendation rests on your waist as a proportion of it."
        suffix="cm"
        placeholder="178"
        value={draft.heightCm}
        onInput={(heightCm) => onChange({ heightCm })}
      />
      <DateField
        label="Date of birth"
        hint="Stored as a date; your age is worked out from it when needed."
        max={today}
        value={draft.dateOfBirth}
        onInput={(dateOfBirth) => onChange({ dateOfBirth })}
      />
    </section>
  )
}

/**
 * Waist and neck, required when body composition is active (D-063, D-090).
 *
 * The route recommendation rests primarily on waist-to-height, so without a
 * waist the wizard could only ever conclude that it has nothing to say. The
 * D-045 protocol is unchanged: exactly three readings, the median saved, and
 * the spread warning at the configured threshold.
 *
 * **This step is not in the list at all for a user who did not choose body
 * composition** — D-090 narrows D-063's "required at first run" to exactly that
 * case. The neck is still asked for and still not demanded: an absent or invalid
 * Navy estimate does not block a route (D-071).
 */
function MeasurementsStep({ draft, onChange }: StepProps) {
  return (
    <section aria-labelledby="step-heading">
      <h1 id="step-heading" class={styles.title}>
        Waist and neck
      </h1>
      <p class={styles.lede}>
        This is the measurement Project Sola plans from. Measure in the morning, before food, drink
        or training. Stand naturally and keep the tape level and snug without compressing the skin.
      </p>
      <p class={styles.hint}>
        Take three readings of each site. Project Sola saves the middle one and tells you when they
        disagree by more than it is comfortable with.
      </p>

      {/* B-003 is unresolved: the exact neck landmark and tape angle are not
          decided. Inventing them here would undermine the repeatability the
          whole estimate rests on, so the gap is stated instead. */}
      <p class={styles.pending}>
        Detailed guidance for exactly where to place the tape has not been finalised yet. Until it
        is, measure the same way every time and note how you did it.
      </p>

      <ReadingTriple
        label="Waist"
        readings={draft.waistReadings}
        onChange={(waistReadings) => onChange({ waistReadings })}
      />
      <ReadingTriple
        label="Neck"
        hint="Required too. The waist is what decides the route; the neck adds the Navy circumference estimate alongside it."
        readings={draft.neckReadings}
        onChange={(neckReadings) => onChange({ neckReadings })}
      />
    </section>
  )
}

/**
 * Starting photos (Checkpoint 3, D-081). Still skippable.
 *
 * **Nothing captured here is written to the database, and that is the whole
 * difficulty of this step.** D-020 requires that reaching the recommendation
 * creates nothing, and a browser test asserts every store is still empty at
 * that point. A photo saved the moment it was taken would break that, so
 * captures are held in memory and written by `confirm()` — after the project
 * they belong to exists.
 *
 * The consequence is stated to the user rather than hidden: a capture does not
 * survive the app being evicted mid-setup, unlike the rest of the draft.
 */
interface PhotosStepProps {
  readonly captured: Partial<Record<PhotoView, Blob>>
  readonly onCaptured: (view: PhotoView, file: Blob) => void
  readonly onRemove: (view: PhotoView) => void
}

function PhotosStep({ captured, onCaptured, onRemove }: PhotosStepProps) {
  const [capturing, setCapturing] = useState<PhotoView | undefined>(undefined)

  if (capturing !== undefined) {
    return (
      <section aria-labelledby="step-heading">
        <h1 id="step-heading" class={styles.title}>
          Starting photos
        </h1>
        <PhotoCapture
          view={capturing}
          ghost={undefined}
          busy={false}
          onCaptured={(file) => {
            onCaptured(capturing, file)
            setCapturing(undefined)
          }}
          onCancel={() => setCapturing(undefined)}
        />
      </section>
    )
  }

  return (
    <section aria-labelledby="step-heading">
      <h1 id="step-heading" class={styles.title}>
        Starting photos
      </h1>
      <p class={styles.lede}>
        Photos are the clearest record of a change a tape measure only hints at. They are optional —
        your project records its starting condition from your measurements either way.
      </p>

      <ul class={styles.photoList}>
        {PHOTO_VIEWS.map((view) => (
          <li key={view} class={styles.photoRow}>
            <span class={styles.photoName}>{PHOTO_VIEW_LABELS[view]}</span>
            {captured[view] === undefined ? (
              <Button onClick={() => setCapturing(view)} variant="secondary">
                Take it
              </Button>
            ) : (
              <>
                <span class={styles.photoReady}>Ready</span>
                <Button
                  onClick={() => onRemove(view)}
                  variant="secondary"
                  label={`Remove the ${PHOTO_VIEW_LABELS[view].toLowerCase()} photo`}
                >
                  Remove
                </Button>
              </>
            )}
          </li>
        ))}
      </ul>

      <p class={styles.pending}>
        These are saved when you confirm your project, not before — Project Sola writes nothing
        until then. If you leave the app in the middle of setup, any photo you have taken here will
        need taking again; the rest of your answers are kept.
      </p>
    </section>
  )
}

/**
 * The two lifestyle questions, and only those two (D-062).
 *
 * Training experience and realistic frequency are asked because the route rules
 * read them. Activity level and sleep quality are **not** asked: nothing
 * consumes the first, and the second duplicates the weekly recovery rating. The
 * converse of D-060 — do not collect what no rule reads — applies on the most
 * abandonment-prone screen in the app.
 */
function TrainingStep({ draft, onChange }: StepProps) {
  return (
    <section aria-labelledby="step-heading">
      <h1 id="step-heading" class={styles.title}>
        How you train
      </h1>
      <p class={styles.lede}>
        Three questions, all of which change what Project Sola recommends or plans. It does not ask
        anything it will not use.
      </p>

      <RadioGroup
        legend="How long have you been training consistently?"
        value={
          TRAINING_EXPERIENCE_LEVELS.includes(draft.trainingExperience as TrainingExperience)
            ? (draft.trainingExperience as TrainingExperience)
            : undefined
        }
        onChange={(trainingExperience) => onChange({ trainingExperience })}
        options={TRAINING_EXPERIENCE_LEVELS.map((level) => ({
          value: level,
          label: TRAINING_EXPERIENCE_LABELS[level],
        }))}
      />

      <NumericField
        label="Sessions a week you realistically train"
        hint="Be honest rather than optimistic — a plan built on sessions that do not happen is worse than one built on the sessions that do."
        placeholder="3"
        value={draft.weeklySessions}
        onInput={(weeklySessions) => onChange({ weeklySessions })}
      />

      {/* Optional, and deliberately so (D-101 §3). It only ever becomes
          `PlannedItem.minutes` on the starter plan; leaving it blank produces a
          plan with session counts and no durations, which is a smaller claim
          than a duration Project Sola picked. */}
      <NumericField
        label="Minutes in a typical session"
        hint="Optional. If you give it, your starting plan will say how long each session is. If you leave it blank, it will just say how often."
        placeholder="45"
        value={draft.minutesPerSession}
        onInput={(minutesPerSession) => onChange({ minutesPerSession })}
      />
    </section>
  )
}

/**
 * Where the nutrition numbers come from (D-096).
 *
 * ────────────────────────────────────────────────────────────────────────────
 *  PROJECT SOLA RECORDS WHAT AN AUTHORITY RECOMMENDS. IT IS NOT THAT AUTHORITY.
 * ────────────────────────────────────────────────────────────────────────────
 *
 * Every number on this screen is one the user read off something else and typed
 * in. **Neither the rules engine nor any AI originates a calorie or protein
 * figure in this version**, so there is no "calculate my targets" control here
 * and adding one would need an approved method, provenance and thresholds — a new
 * decision, not a new button.
 *
 * **The source list is provider-neutral and the name is free text.** Building
 * against one product's vocabulary would couple the app to a company; "an
 * external app, which I call MacroFactor" costs nothing and works for a
 * spreadsheet or a coach just as well.
 *
 * **The targets are optional even for a source that has one.** A coach may hand
 * over a plan with no calorie figure in it, and that is a real plan the user can
 * follow and report on — `planKind` is derived from whether any figure was given
 * rather than asked as a second question the two answers could disagree about.
 */
function NutritionStep({ draft, onChange }: StepProps) {
  const source = draft.nutritionSource
  const chosen = source !== ''
  const hasPlan = chosen && source !== 'none'

  return (
    <section aria-labelledby="step-heading">
      <h1 id="step-heading" class={styles.title}>
        Your nutrition targets
      </h1>
      <p class={styles.lede}>
        Project Sola records what your targets are and whether you met them. It does not work them
        out, and it is not a food diary — there is nowhere here to log a meal.
      </p>

      <RadioGroup
        legend="Where do your calorie and protein targets come from?"
        value={isNutritionSource(source) ? source : undefined}
        onChange={(nutritionSource) => onChange({ nutritionSource })}
        options={NUTRITION_SOURCES.map((value) => ({
          value,
          label: NUTRITION_SOURCE_LABELS[value],
        }))}
      />

      {hasPlan ? (
        <>
          <TextField
            label="What do you call it? (optional)"
            hint="For your own reference — a name like “MacroFactor” or “Anna”. It is treated as a personal note and left out of exports unless you include notes."
            value={draft.nutritionProviderLabel}
            onInput={(nutritionProviderLabel) => onChange({ nutritionProviderLabel })}
          />

          <NumericField
            label="Calorie target (optional)"
            hint="Leave blank if your plan has no calorie figure."
            placeholder="2400"
            value={draft.nutritionCalorieTarget}
            onInput={(nutritionCalorieTarget) => onChange({ nutritionCalorieTarget })}
          />

          {draft.nutritionCalorieTarget.trim() === '' ? null : (
            <RadioGroup
              legend="Is that per day or a weekly average?"
              value={
                isCalorieTargetBasis(draft.nutritionCalorieBasis)
                  ? draft.nutritionCalorieBasis
                  : undefined
              }
              onChange={(nutritionCalorieBasis) => onChange({ nutritionCalorieBasis })}
              options={[
                { value: 'perDay', label: 'Per day' },
                { value: 'weeklyAverage', label: 'A weekly average' },
              ]}
            />
          )}

          <NumericField
            label="Protein target in grams (optional)"
            placeholder="180"
            value={draft.nutritionProteinTargetG}
            onInput={(nutritionProteinTargetG) => onChange({ nutritionProteinTargetG })}
          />

          <NumericField
            label="Weekly rate it is set to, as a percentage (optional)"
            hint="Negative to lose, positive to gain — for example −0.5. If this disagrees with the phase Project Sola plans, it will show you both and change neither."
            placeholder="-0.5"
            value={draft.nutritionConfiguredRatePercent}
            onInput={(nutritionConfiguredRatePercent) =>
              onChange({ nutritionConfiguredRatePercent })
            }
          />

          {source === 'externalNutritionApp' ? (
            <label class={styles.checkboxRow}>
              <input
                type="checkbox"
                checked={draft.nutritionDailyChecksEnabled}
                onChange={(event) =>
                  onChange({
                    nutritionDailyChecksEnabled: (event.currentTarget as HTMLInputElement).checked,
                  })
                }
              />
              <span>
                Ask me daily whether I met these targets. Off by default — you are already logging
                in that app, and Project Sola will not ask you to do it twice.
              </span>
            </label>
          ) : null}
        </>
      ) : null}
    </section>
  )
}

/**
 * The destination, and the date.
 *
 * **No route question.** The options are outcomes; Project Sola works out what
 * to do about them. Recomposition is named as unavailable on the plan screen
 * rather than being silently missing from this list (D-062).
 *
 * **The outcome radio appears only when body composition is active.** Its
 * vocabulary is body-composition destinations — visible abs, lose belly fat — so
 * asking a mobility-only user to pick one would be asking about an area they
 * declined. They are asked for a date and nothing else.
 */
function DestinationStep({ draft, today, onChange }: StepProps & { today: string }) {
  const isEvent = draft.desiredOutcome === 'prepareForEvent'
  const plansBody = willPlanBodyComposition(draft)

  return (
    <section aria-labelledby="step-heading">
      <h1 id="step-heading" class={styles.title}>
        What are you aiming for?
      </h1>
      <p class={styles.lede}>
        {plansBody
          ? 'Describe where you want to get to. Project Sola works out how to get there and explains why — you never have to pick between a cut, a maintenance block and a lean bulk.'
          : 'Give yourself a horizon to work towards. Reaching it changes nothing on its own — Project Sola will tell you it has arrived and leave the decision to you.'}
      </p>

      {plansBody ? (
        <RadioGroup
          legend="Desired outcome"
          value={
            DESIRED_OUTCOMES.includes(draft.desiredOutcome as DesiredOutcome)
              ? (draft.desiredOutcome as DesiredOutcome)
              : undefined
          }
          onChange={(desiredOutcome) => onChange({ desiredOutcome })}
          options={DESIRED_OUTCOMES.map((outcome) => ({
            value: outcome,
            label: DESIRED_OUTCOME_LABELS[outcome],
          }))}
        />
      ) : null}

      {/* One date, not two. "Two dates in three steps read as a mistake" was a
          real tester's words at Checkpoint 1.8; for an event-driven project the
          event IS the target, so it is asked once. */}
      <DateField
        label={isEvent ? 'When is the event?' : 'When do you want to be there?'}
        hint={
          isEvent
            ? 'Project Sola checks whether there is enough time to prepare sensibly, and says so if there is not.'
            : 'A rough date is fine. You can move it later, and reaching it changes nothing on its own.'
        }
        min={today}
        value={draft.targetDate}
        onInput={(targetDate) => onChange({ targetDate })}
      />

      {/* A second date, and a different question from the one above (D-107).
          The tester's objection was to being asked for the event and the target
          separately — one thing asked twice. When the project begins is not that.
          Blank means today, which is why nothing is prefilled. */}
      <DateField
        label="When do you want to start?"
        hint="Today, or up to a week from now. Project Sola waits until then — you can still record your weight and measurements in the meantime."
        min={today}
        max={latestStart(today)}
        value={draft.startDate}
        onInput={(startDate) => onChange({ startDate })}
      />

      {/*
        Three notices, one summary, still visible (D-104 §6).
        ────────────────────────────────────────────────────────────────────────
        **All three are on D-104 §4's list, so none of them may move behind a
        control** (D-021, D-033, D-061). What changed is that each is a sentence
        rather than a paragraph, with the consequences behind the one
        information control — which is the difference between a user reading
        them and a user scrolling past three blocks of grey text.

        The distinction worth keeping: the *claim* is visible, the *elaboration*
        is one tap away. A user who reads only what is on screen still learns
        that nothing is uploaded, that local storage can be lost, and that the
        installed copy is separate.
      */}
      <div class={styles.notices}>
        <div class={styles.sectionHead}>
          <h2 class={styles.sectionHeading}>Before you start</h2>
          <InfoButton topic="how your data is kept" title="How your data is kept">
            <p>
              <strong>Nothing is uploaded and there is no account.</strong> Anyone who can use this
              unlocked phone or browser profile may be able to open Project Sola and see your data.
            </p>
            <p>
              <strong>Local storage is not permanent.</strong> Clearing this browser&rsquo;s site
              data, removing the browser, losing the device, or the browser reclaiming space can
              destroy your data. Make a backup regularly from Settings, and keep it somewhere you
              trust.
            </p>
            <p>
              <strong>An installed copy keeps its own data.</strong> If you add Project Sola to your
              Home Screen, opening the same address in Safari will look empty. Nothing has been lost
              — they are simply separate. Pick one and stay with it, or move your data across with a
              backup file.
            </p>
          </InfoButton>
        </div>

        <p class={styles.notice}>Your data stays on this device. Nothing is uploaded.</p>
        <p class={styles.notice}>
          Local storage is not permanent — back up regularly from Settings.
        </p>
        <p class={styles.notice}>
          Added to your Home Screen? That copy keeps its own data, separately.
        </p>
      </div>
    </section>
  )
}

/**
 * The plan, and the explicit confirmation that follows it.
 *
 * The order is fixed and it is the product: the route, then the reasoning, then
 * a redirect if there was one, then what the project is aiming at — and only
 * then a button. **Pressing that button is what creates the project** (D-020).
 * Nothing before it writes anything.
 *
 * **Every active area is listed, whether or not Project Sola can judge it.** A
 * cardio goal with no approved rules says so, in the honest `baselineRequired`
 * form, rather than being shown a plan nobody derived — D-060 applied to the plan
 * screen.
 */
function PlanStep({
  draft,
  recommendation,
  outlook,
  onChange,
}: StepProps & {
  recommendation: RouteRecommendation | undefined
  outlook: RouteOutlook | undefined
}) {
  const domains = selectedDomainsOf(draft)

  return (
    <section aria-labelledby="step-heading">
      <h1 id="step-heading" class={styles.title}>
        {recommendation === undefined ? 'Here is your plan' : 'Here is the route'}
      </h1>

      {recommendation === undefined ? null : (
        <RouteSummary recommendation={recommendation} outlook={outlook} />
      )}

      <h2 class={styles.sectionHeading}>What Project Sola will track</h2>
      <ul class={styles.domainList}>
        {domains.map((domain) => (
          <li key={domain} class={styles.domainRow}>
            <span class={styles.domainName}>{FITNESS_DOMAIN_LABELS[domain]}</span>
            <span class={styles.domainHint}>
              {domain === 'bodyComposition'
                ? 'Weekly review, with a recommendation each week.'
                : 'Recorded and shown. Project Sola will not judge it yet — it needs a baseline first.'}
            </span>
          </li>
        ))}
      </ul>

      <StarterWeek draft={draft} />

      <TextField
        label="Project name"
        hint="Yours to change. It is how this shows up in your history later."
        placeholder={recommendation?.suggestedProject.name ?? 'Project'}
        value={draft.projectName}
        onInput={(projectName) => onChange({ projectName })}
      />

      {/* Named as unavailable rather than silently omitted (D-062). Someone who
          has heard of recomposition and sees no mention of it assumes the app
          forgot. */}
      <div class={styles.notices}>
        {(recommendation?.notPlanned ?? []).map((item) => (
          <p key={item.capability} class={styles.notice}>
            <strong>{item.capability}</strong> {item.reason}
          </p>
        ))}
        {domains.some((domain) => domain !== 'bodyComposition') ? (
          <p class={styles.notice}>
            <strong>Weekly advice covers body composition only for now.</strong> Project Sola
            records everything you chose, and says nothing it cannot back up. Advice for the other
            areas arrives when the rules behind it have been approved.
          </p>
        ) : null}
        <p class={styles.notice}>
          <strong>Nothing has been created yet.</strong> Project Sola never starts a project on your
          behalf. Reading this changed nothing; the button below is what begins it.
        </p>
      </div>
    </section>
  )
}

/**
 * The starting week, shown before anything is created (D-101).
 *
 * **Every figure on screen is one the user typed**, which is what the lede says
 * out loud and what keeps this a plan rather than a prescription. **No copy here
 * may imply Project Sola has judged, endorsed or optimised it** — it has not, and
 * the list above still says which domains it will not form an opinion about.
 *
 * Renders nothing when there is no starter — a body-composition-only or
 * mobility-only setup — rather than an empty heading. An absent section is honest;
 * a heading over nothing reads as a feature that failed to load.
 */
function StarterWeek({ draft }: { draft: ProjectSetupDraft }) {
  const starters = buildSetupStarterCommitments(draft)
  const planned = orderDomains(Object.keys(starters).filter(isFitnessDomain))

  if (planned.length === 0) return null

  return (
    <>
      {/* The derivation is what makes this a plan rather than a prescription
          (D-101 §3), and it is one tap away rather than gone: every figure below
          is one the user typed, and the sheet is where that is said. */}
      <div class={styles.sectionHead}>
        <h2 class={styles.sectionHeading}>Your first week</h2>
        <InfoButton topic="where this week came from" title="Where this week came from">
          <p>
            Built from what you just told us — the sessions a week you said you realistically train,
            split across what you chose to work on.
          </p>
          <p>
            Project Sola has not judged, endorsed or optimised it. Every figure here is one you
            typed. It is a starting point and it is yours to change at any time in My Plan.
          </p>
        </InfoButton>
      </div>
      <ul class={styles.domainList}>
        {planned.map((domain) => {
          const item = starters[domain]?.standard[0]
          return (
            <li key={domain} class={styles.domainRow}>
              <span class={styles.domainName}>{FITNESS_DOMAIN_LABELS[domain]}</span>
              <span class={styles.domainHint}>
                {item === undefined
                  ? null
                  : `${item.timesPerWeek} a week${
                      item.minutes === undefined ? '' : `, about ${item.minutes} minutes each`
                    }.`}
              </span>
            </li>
          )
        })}
      </ul>
    </>
  )
}

/**
 * How a route recommendation is presented, in one place.
 *
 * Exported at D-103 §8 so the phase-end flow shows the same thing rather than a
 * second arrangement of the same fields. **The outlook renders wherever a
 * recommendation does** — that is §8's amendment to D-102 §2, and it holds
 * because this component carries it, not because two screens remembered to.
 */
export function RouteSummary({
  recommendation,
  outlook,
}: {
  recommendation: RouteRecommendation
  outlook: RouteOutlook | undefined
}) {
  const { condition, duration, weeklyTargetPercent } = recommendation

  return (
    <>
      <div class={styles.route}>
        <p class={styles.routeLabel}>Recommended</p>
        <h2 class={styles.routeName}>{PHASE_LABELS[recommendation.recommendedPhase]}</h2>
        <p class={styles.routeObjective}>{recommendation.primaryObjective}</p>
      </div>

      {recommendation.redirect === undefined ? null : (
        <div class={styles.redirect}>
          <div class={styles.sectionHead}>
            <h2 class={styles.redirectHeading}>
              Not a {PHASE_LABELS[recommendation.redirect.insteadOf].toLowerCase()}
            </h2>
            <InfoButton
              topic="this redirect"
              title={`Not a ${PHASE_LABELS[recommendation.redirect.insteadOf].toLowerCase()}`}
            >
              <p>{recommendation.redirect.because}</p>
            </InfoButton>
          </div>
        </div>
      )}

      {/*
        **One sentence stays, the list moves** (D-104 §6). The clause in
        `ui-ux-spec.md` that this amends said the basis for a recommendation is
        never "a disclosure the user has to go looking for" — and the reasoning
        that survives is why: the primary reason is beside the route, so the
        screen is answerable without opening anything. What went behind the
        control is the *rest* of a list that ran to four or five lines.

        The heading stays even though its list did not. `setup.spec.ts` asserts
        the outlook renders after it, and a heading that disappears would leave
        that assertion comparing against −1 and passing whatever happened — the
        guard-that-cannot-fail shape, arrived at by deletion.
      */}
      <div class={styles.sectionHead}>
        <h2 class={styles.sectionHeading}>Why</h2>
        <InfoButton topic="this route" title="Why this route">
          <ul class={styles.reasons}>
            {recommendation.reasoning.map((reason) => (
              <li key={reason}>{reason}</li>
            ))}
          </ul>
          <p>{recommendation.confidenceReason}</p>
          <p>
            Waist as a proportion of height is your waist measurement divided by your height, taken
            from the middle of your three readings. Project Sola uses it as the primary evidence for
            which route to take, because it needs no scale and does not move with a single heavy
            meal.
          </p>
        </InfoButton>
      </div>
      <p class={styles.primaryReason}>{recommendation.reasoning[0]}</p>

      <h2 class={styles.sectionHeading}>The plan</h2>
      <div class={styles.facts}>
        <p class={styles.factLabel}>Waist as a proportion of height</p>
        <p class={styles.factValue}>{condition.waistToHeight.toFixed(2)}</p>

        <p class={styles.factLabel}>Weekly weight change to aim for</p>
        <p class={styles.factValue}>
          {weeklyTargetPercent.min}% to {weeklyTargetPercent.max}%
        </p>

        <p class={styles.factLabel}>Length</p>
        <p class={styles.factValue}>
          {formatWeekCount(duration.weeks)} · to {formatApproximateMonth(duration.approximateMonth)}
        </p>

        <p class={styles.factLabel}>How sure Project Sola is</p>
        <p class={styles.factValue}>{recommendation.confidence}</p>
      </div>

      <h2 class={styles.sectionHeading}>What this project is aiming at</h2>
      <ul class={styles.criteria}>
        {recommendation.successCriteria.map((criterion) => (
          <li key={criterion}>{criterion}</li>
        ))}
      </ul>

      {outlook === undefined ? null : (
        /* D-102. Below everything the recommendation says, deliberately: the
           route the user is being asked to confirm is the decision on this
           screen, and a conditional future placed above it would compete with
           the present one. The same ordering reasoning as D-008's second
           opinion, applied to Sola's own forecast. */
        <div class={styles.outlook}>
          <div class={styles.sectionHead}>
            <h2 class={styles.sectionHeading}>After this phase</h2>
            <InfoButton topic="the phase after this one" title="After this phase">
              <p>{outlook.because}</p>
            </InfoButton>
          </div>
          <p class={styles.outlookNext}>
            {outlook.condition}, Project Sola would expect to recommend a{' '}
            <strong>{PHASE_LABELS[outlook.nextPhase].toLowerCase()}</strong>.
          </p>
          {/* Not decoration. Nothing here is stored, and the engine decides
              again from real evidence when this phase ends — so it may name a
              different phase then. Saying that here is cheaper than reading as
              inconsistent later. */}
          <p class={styles.hint}>
            Nothing about this is fixed or saved. When this phase ends, Project Sola looks at what
            actually happened and decides again.
          </p>
        </div>
      )}
    </>
  )
}

/* ── Pieces ─────────────────────────────────────────────────────────────── */

/**
 * Three readings for one site (D-045).
 *
 * A deliberate second implementation rather than a reuse of the Review
 * screen's `MeasurementCapture`: that component saves to the database the
 * moment its form is submitted and shows the history beneath it, and setup must
 * do neither before the user confirms. The part that actually matters — the
 * median, the spread and the threshold warning — is the shared
 * `summariseSites`, so there is one implementation of the protocol and two of a
 * row of three inputs.
 */
export function ReadingTriple({
  label,
  hint,
  readings,
  onChange,
}: {
  label: string
  hint?: string
  readings: readonly string[]
  onChange: (readings: readonly string[]) => void
}) {
  return (
    <fieldset class={styles.site}>
      <legend class={styles.siteLabel}>{label}</legend>
      {hint === undefined ? null : <p class={styles.hint}>{hint}</p>}
      <div class={styles.readings}>
        {Array.from({ length: READINGS_PER_SITE }, (_, index) => (
          <NumericField
            key={`${label}-${index}`}
            label={`${label} reading ${index + 1}`}
            suffix="cm"
            value={readings[index] ?? ''}
            onInput={(value) => {
              const next = [...readings]
              next[index] = value
              onChange(next)
            }}
          />
        ))}
      </div>
    </fieldset>
  )
}

/* ── Per-step validation ────────────────────────────────────────────────── */

/*
 * These messages are what the user sees, so each names the field and the
 * correction. The model validators run again on save — this layer catches a
 * mistake before the step advances, it does not replace them.
 *
 * Keyed by step rather than by index, for the same reason the draft is: an index
 * means something different for each user's computed list.
 */

export function validateStep(
  step: SetupStep,
  draft: ProjectSetupDraft,
  today: string,
): string | undefined {
  if (step === 'domains') return validateDomains(draft)
  if (step === 'intentions') return validateIntentions(draft)
  if (step === 'confirmPreset') return validateDomains(draft)
  if (step === 'aboutYou') return validateAboutYou(draft)
  if (step === 'measurements') return validateMeasurements(draft)
  if (step === 'training') return validateTraining(draft)
  if (step === 'destination') return validateDestination(draft, today)
  if (step === 'nutrition') return validateNutrition(draft)
  return undefined
}

/**
 * The nutrition step's own coherence (D-096).
 *
 * The plausibility bounds are **not** checked here — they live in
 * `validateNutritionPlan`, which the write path runs, and duplicating them would
 * create a second set of numbers to keep in step with the first. What this checks
 * is the two things that would otherwise let the user past a screen they have not
 * answered: an unchosen source, and a calorie figure with no basis.
 */
function validateNutrition(draft: ProjectSetupDraft): string | undefined {
  if (draft.nutritionSource === '') {
    return 'Say where your nutrition targets come from. Choose “No nutrition target” if you do not have one.'
  }
  if (draft.nutritionSource === 'none') return undefined

  for (const [field, value] of [
    ['Calorie target', draft.nutritionCalorieTarget],
    ['Protein target', draft.nutritionProteinTargetG],
    ['Weekly rate', draft.nutritionConfiguredRatePercent],
  ] as const) {
    if (value.trim() !== '' && !Number.isFinite(Number(value))) {
      return `${field} must be a number, or left blank.`
    }
  }

  /* A daily figure and a weekly average differ by a factor of seven, and comparing
     them silently is a wrong answer rather than a rounding error. */
  if (draft.nutritionCalorieTarget.trim() !== '' && draft.nutritionCalorieBasis === '') {
    return 'Say whether that calorie figure is per day or a weekly average.'
  }
  return undefined
}

function validateDomains(draft: ProjectSetupDraft): string | undefined {
  if (selectedDomainsOf(draft).length === 0) {
    return 'Choose at least one area to work on. You can add more later.'
  }
  return undefined
}

function validateIntentions(draft: ProjectSetupDraft): string | undefined {
  const selected = selectedDomainsOf(draft)
  const unanswered = selected.filter((domain) => draft.intentions[domain] === undefined)
  if (unanswered.length > 0) {
    return `Say how much you want to do about ${unanswered
      .map((domain) => FITNESS_DOMAIN_LABELS[domain].toLowerCase())
      .join(' and ')}.`
  }

  /* A main focus is required only when something can be one. A user who set every
     area to "track only" has nothing to focus on, and demanding one would be
     asking them to contradict the answer they just gave. */
  const focusable = selected.filter((domain) => draft.intentions[domain] !== 'trackOnly')
  if (focusable.length > 0 && primaryOf(draft) === undefined) {
    return 'Choose which area is your main focus.'
  }
  return undefined
}

function validateAboutYou(draft: ProjectSetupDraft): string | undefined {
  if (draft.heightCm.trim() === '') return 'Enter your height in centimetres.'
  if (!Number.isFinite(Number(draft.heightCm))) return 'Height must be a number, for example 178.'
  if (draft.dateOfBirth === '') return 'Enter your date of birth.'
  return undefined
}

function validateMeasurements(draft: ProjectSetupDraft): string | undefined {
  const waist = draft.waistReadings.filter((value) => value.trim() !== '')
  if (waist.length !== READINGS_PER_SITE) {
    return `Enter all ${READINGS_PER_SITE} waist readings. The recommendation rests on your waist, so it cannot be skipped.`
  }
  if (waist.some((value) => !Number.isFinite(Number(value)))) {
    return 'Waist readings must be numbers, for example 88.4.'
  }

  /* Both sites are required when body composition is active (D-063, narrowed by
     D-090). The neck does not decide the route, but it is what turns the
     measurements into a body-composition estimate, and asking for it later means a
     starting condition with a hole in it that can never be filled
     retrospectively. */
  const neck = draft.neckReadings.filter((value) => value.trim() !== '')
  if (neck.length !== READINGS_PER_SITE) {
    return `Enter all ${READINGS_PER_SITE} neck readings. Both sites are needed to record where you are starting from.`
  }
  if (neck.some((value) => !Number.isFinite(Number(value)))) {
    return 'Neck readings must be numbers, for example 38.1.'
  }
  return undefined
}

function validateTraining(draft: ProjectSetupDraft): string | undefined {
  if (draft.trainingExperience === '') return 'Choose how long you have been training.'
  if (draft.weeklySessions.trim() === '') {
    return 'Enter how many sessions a week you realistically train.'
  }
  const sessions = Number(draft.weeklySessions)
  if (!Number.isFinite(sessions) || sessions < 0) {
    return 'Sessions a week must be a number, for example 3.'
  }
  /* Blank is a real answer and must pass (D-101 §3). Only a value the user
     actually typed is checked, and it is checked rather than coerced, because a
     bad figure silently becoming a default is how a number nobody stated ends up
     in the plan. */
  if (draft.minutesPerSession.trim() !== '') {
    const minutes = Number(draft.minutesPerSession)
    if (!Number.isFinite(minutes) || minutes <= 0) {
      return 'Minutes in a typical session must be a positive number, for example 45.'
    }
  }
  return undefined
}

function validateDestination(draft: ProjectSetupDraft, today: string): string | undefined {
  /* Only asked when body composition is active, so only required then. */
  if (willPlanBodyComposition(draft) && draft.desiredOutcome === '') {
    return 'Choose what you are aiming for.'
  }
  if (draft.targetDate === '') return 'Enter the date you are working towards.'
  if (draft.targetDate < today) return 'The date must not be in the past.'
  return startDateIssue(draft, today)
}

/**
 * The start date's rules (D-107 §7).
 *
 * **This is the enforcement; the field's `max` is the affordance.** A native date
 * input treats `max` as advisory in some engines, so a bound that only lives on
 * the element is not a bound. Both are tested.
 */
/**
 * The latest start date the wizard accepts, as the same kind of string the rest
 * of this step works in.
 *
 * A `today` that will not parse falls back to itself, which degrades to
 * "today only" — the behaviour before D-107 — rather than to an unreachable
 * branch nothing can exercise.
 */
function latestStart(today: string): string {
  const parsed = toCalendarDate(today)
  return parsed === undefined ? today : unwrapDate(latestSetupStartDate(parsed))
}

function startDateIssue(draft: ProjectSetupDraft, today: string): string | undefined {
  // Blank is a real answer and means today.
  if (draft.startDate === '') return undefined
  if (draft.startDate < today) return 'The start date must not be in the past.'
  if (draft.startDate > latestStart(today)) {
    return 'A project can start at most a week from now, because the route comes from the measurements you just recorded.'
  }
  if (draft.targetDate !== '' && draft.targetDate < draft.startDate) {
    return 'The date you are working towards must not be before the start date.'
  }
  return undefined
}
